import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  Tooltip,
  Typography,
  Stack,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { toast } from "react-toastify";

import { PatchSchedule } from "../../../services/fhir";

// URL da nova extensão de exposição específica da agenda
// Mantém o mesmo padrão das outras extensões de Schedule já existentes
const EXTENSION_SCHEDULE_EXPOSURE_URL =
  "https://fluxmed.com.br/fhir/StructureDefinition/schedule-exposure";

interface ScheduleExposureToggleProps {
  // BundleEntry<fhir5.Schedule> - mantido como any para seguir padrão atual do módulo
  schedule: any;
  // Função opcional chamada após salvar com sucesso (usada para forçar refetch externo)
  onUpdated?: () => void;
}

export function ScheduleExposureToggle({
  schedule,
  onUpdated,
}: ScheduleExposureToggleProps) {
  const scheduleResource = schedule?.resource;

  // Lê o valor atual da extensão no Schedule vindo do servidor
  const serverExtensions = (scheduleResource?.extension || []) as any[];
  const serverExposureIndex = serverExtensions.findIndex(
    (ext: any) => ext?.url === EXTENSION_SCHEDULE_EXPOSURE_URL,
  );

  // Caso a extensão não exista, consideramos o padrão como "exposto" (true)
  // Isso mantém compatibilidade: agendas antigas (sem extensão) são tratadas
  // como habilitadas para exposição até que o usuário altere explicitamente.
  const serverExposure =
    serverExposureIndex >= 0
      ? !!serverExtensions[serverExposureIndex]?.valueBoolean
      : true;

  // Estado local exibido no switch (espelha o valor efetivo salvo)
  const [currentExposure, setCurrentExposure] =
    useState<boolean>(serverExposure);

  // Estados para o diálogo de confirmação
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingExposure, setPendingExposure] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);

  // Sempre que o Schedule mudar (refetch externo), sincroniza o estado local
  useEffect(() => {
    setCurrentExposure(serverExposure);
  }, [serverExposure, scheduleResource?.id]);

  /**
   * Abre o diálogo de confirmação guardando o valor desejado.
   * Não aplica a alteração imediatamente para evitar mudanças acidentais.
   */
  const handleRequestChange = (desiredExposure: boolean) => {
    setPendingExposure(desiredExposure);
    setDialogOpen(true);
  };

  /**
   * Fecha o diálogo sem aplicar alterações.
   */
  const handleCancel = () => {
    setDialogOpen(false);
    setPendingExposure(null);
  };

  /**
   * Confirma a alteração de exposição da agenda e aplica via PatchSchedule.
   * Edita exclusivamente a extensão de exposição, sem tocar nas demais extensões.
   */
  const handleConfirm = async () => {
    if (!scheduleResource?.id || pendingExposure === null) {
      handleCancel();
      return;
    }

    const newExposure = pendingExposure;
    const extensions = (scheduleResource.extension || []) as any[];

    const existingIndex = extensions.findIndex(
      (ext: any) => ext?.url === EXTENSION_SCHEDULE_EXPOSURE_URL,
    );

    // Monta a extensão FHIR com o novo valor booleano
    const exposureExtension: fhir5.Extension = {
      url: EXTENSION_SCHEDULE_EXPOSURE_URL,
      valueBoolean: newExposure,
    };

    const patchOperations: any[] = [];

    // Se já existe uma extensão de exposição, apenas a substituímos
    if (existingIndex >= 0) {
      patchOperations.push({
        op: "replace",
        path: `/extension/${existingIndex}`,
        value: exposureExtension,
      });
    } else if (extensions && extensions.length > 0) {
      // Já há outras extensões: adiciona a nova no final do array
      patchOperations.push({
        op: "add",
        path: "/extension/-",
        value: exposureExtension,
      });
    } else {
      // Não há nenhuma extensão ainda: cria o array de extensões com a nova
      patchOperations.push({
        op: "add",
        path: "/extension",
        value: [exposureExtension],
      });
    }

    if (!patchOperations.length) {
      handleCancel();
      return;
    }

    try {
      setSaving(true);
      await PatchSchedule(scheduleResource.id, patchOperations);

      // Atualiza o estado local imediatamente para refletir a nova configuração
      setCurrentExposure(newExposure);
      toast.success(
        "Configuração de exposição da agenda atualizada com sucesso!",
      );

      // Permite que o componente pai faça um refetch se desejar
      if (onUpdated) {
        onUpdated();
      }
    } catch (error) {
      console.error("Erro ao atualizar exposição da agenda:", error);
      toast.error(
        "Não foi possível salvar a configuração de exposição da agenda.",
      );
    } finally {
      setSaving(false);
      handleCancel();
    }
  };

  const isExposed = currentExposure;

  const dialogTitle = isExposed
    ? "Tornar esta agenda privada"
    : "Tornar esta agenda pública";

  const dialogDescription = isExposed
    ? "Ao tornar esta agenda privada, ela deixará de ser exibida em portais públicos e integrações que respeitam essa configuração de exposição."
    : "Ao tornar esta agenda pública, ela poderá ser exibida em portais públicos e integrações compatíveis.";

  return (
    <>
      {/* Wrapper com stopPropagation para não abrir/fechar o Accordion ao clicar no controle.
          Aqui também aplicamos o estilo de "pill" para dar mais destaque visual ao toggle. */}
      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{
          width: "15%",
          display: "flex",
          alignItems: "center",
          px: 1.5,
          py: 0.5,
          borderRadius: 999,
          bgcolor: isExposed ? "primary" : "grey.100",
          border: "1px solid",
          borderColor: isExposed ? "primary.main" : "grey.300",
        }}
      >
        <Tooltip
          title={
            isExposed
              ? "Agenda pública (marcada para exposição em portais e integrações compatíveis)"
              : "Agenda privada (não exibida em portais públicos nem em integrações que respeitam essa configuração)"
          }
        >
          {/* Layout interno compacto: switch + texto, em uma pequena cápsula destacada */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Switch
              size="small"
              color="primary"
              checked={isExposed}
              onChange={() => handleRequestChange(!isExposed)}
            />

            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Agenda {isExposed ? "Pública" : "Privada"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isExposed ? "Exposição ativada" : "Exposição desativada"}
              </Typography>
            </Box>
          </Stack>
          {/* Versão anterior com FormControlLabel e ícones foi simplificada
              para reduzir ruído visual e caber melhor no cabeçalho. */}
          {/*
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="primary"
                checked={isExposed}
                onChange={() => handleRequestChange(!isExposed)}
              />
            }
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {isExposed ? (
                  <Visibility sx={{ fontSize: 18 }} />
                ) : (
                  <VisibilityOff sx={{ fontSize: 18 }} />
                )}
                <Typography variant="body2">
                  Exposição da agenda
                </Typography>
              </Box>
            }
          />
          */}
        </Tooltip>
      </Box>

      {dialogOpen && (
        <Dialog
          open={dialogOpen}
          onClose={(_, reason) => {
            // Evita fechar por clique no backdrop para forçar decisão explícita
            if (reason !== "backdropClick") handleCancel();
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mt: 1 }}>
              {dialogDescription}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancel} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color={isExposed ? "error" : "primary"}
              onClick={handleConfirm}
              disabled={saving}
            >
              {saving ? "Salvando..." : "Confirmar"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
